import React, { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';
import { 
  Users, Check, X, Eye, Search, RefreshCw, Mail, Phone, 
  UserCheck, UserX, ToggleLeft, ToggleRight, AlertCircle,
  ShieldOff, ShieldCheck, Ban, Clock
} from 'lucide-react';

/**
 * ========================================
 * DRIVERS MANAGEMENT ADMIN
 * ========================================
 * View and manage all drivers - activate/deactivate/block
 * 
 * STATUSES:
 *   active   – Driver can log in and accept deliveries
 *   inactive – Driver is deactivated (soft, can be reactivated)
 *   blocked  – Driver is blocked from accepting deliveries (hard block with reason)
 * 
 * DB COLUMNS NEEDED ON `drivers` TABLE:
 *   status         text         ('active' | 'inactive' | 'blocked')
 *   blocked_reason text         (nullable - reason for blocking)
 *   blocked_at     timestamptz  (nullable)
 *   blocked_by     text         (nullable - admin who blocked)
 */

const DriversManagementAdmin = () => {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('active');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Block modal state
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [blockReason, setBlockReason] = useState('');
  const [driverToBlock, setDriverToBlock] = useState(null);

  // Confirmation modal state
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);

  // Fetch drivers
  const fetchDrivers = async () => {
    try {
      setLoading(true);
      
      // Step 1: Fetch drivers directly (no joins that can fail)
      let query = supabase
        .from('drivers')
        .select('*')
        .order('created_at', { ascending: false });

      if (filter !== 'all') {
        query = query.eq('status', filter);
      }

      const { data: driversData, error: driversError } = await query;

      if (driversError) throw driversError;

      if (!driversData || driversData.length === 0) {
        setDrivers([]);
        return;
      }

      // Step 2: Try to fetch matching driver_applications separately
      // Use user_id or application_id — whichever links them
      const userIds = driversData.map(d => d.user_id).filter(Boolean);
      const driverIds = driversData.map(d => d.id).filter(Boolean);
      
      let applicationsMap = {};

      if (userIds.length > 0) {
        // Try matching by user_id first
        const { data: apps } = await supabase
          .from('driver_applications')
          .select('user_id, full_name, email, phone, photo_url, id_number, id_type, bike_registration')
          .in('user_id', userIds);

        if (apps && apps.length > 0) {
          apps.forEach(app => {
            applicationsMap[app.user_id] = app;
          });
        }
      }

      // Step 3: Merge application data into drivers
      const merged = driversData.map(driver => ({
        ...driver,
        driver_applications: applicationsMap[driver.user_id] || null
      }));

      setDrivers(merged);
    } catch (err) {
      console.error('Error fetching drivers:', err);
      setError('Failed to load drivers: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDrivers();
  }, [filter]);

  // Filter by search
  const filteredDrivers = drivers.filter(driver => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    const appInfo = driver.driver_applications;
    return (
      driver.name?.toLowerCase().includes(query) ||
      driver.phone?.includes(query) ||
      appInfo?.email?.toLowerCase().includes(query) ||
      appInfo?.bike_registration?.toLowerCase().includes(query)
    );
  });

  // View driver details
  const handleView = (driver) => {
    setSelectedDriver(driver);
    setShowModal(true);
  };

  // Toggle driver status (active/inactive)
  const handleToggleStatus = async (driver, newStatus) => {
    // If blocking, open block modal instead
    if (newStatus === 'blocked') {
      setDriverToBlock(driver);
      setBlockReason('');
      setShowBlockModal(true);
      return;
    }

    // For activate/deactivate, show confirmation
    setConfirmAction({
      driver,
      newStatus,
      title: newStatus === 'active' ? 'Activate Driver' : 'Deactivate Driver',
      message: newStatus === 'active'
        ? `Are you sure you want to activate ${driver.name}? They will be able to accept deliveries again.`
        : `Are you sure you want to deactivate ${driver.name}? They will not be able to accept any new deliveries.`,
      buttonText: newStatus === 'active' ? 'Yes, Activate' : 'Yes, Deactivate',
      buttonClass: newStatus === 'active' ? 'bg-green-600 hover:bg-green-700' : 'bg-orange-600 hover:bg-orange-700',
    });
    setShowConfirmModal(true);
  };

  // Execute the confirmed action
  const executeStatusChange = async (driver, newStatus, reason = null) => {
    setProcessing(true);
    setError('');

    try {
      const updateData = {
        status: newStatus,
        updated_at: new Date().toISOString()
      };

      // If blocking, add block metadata
      if (newStatus === 'blocked') {
        updateData.blocked_reason = reason || 'No reason provided';
        updateData.blocked_at = new Date().toISOString();
        updateData.blocked_by = 'admin'; // Replace with actual admin user if you have auth
      }

      // If unblocking/activating, clear block metadata
      if (newStatus === 'active') {
        updateData.blocked_reason = null;
        updateData.blocked_at = null;
        updateData.blocked_by = null;
      }

      const { error: updateError } = await supabase
        .from('drivers')
        .update(updateData)
        .eq('id', driver.id);

      if (updateError) throw updateError;

      const statusLabels = {
        active: 'activated',
        inactive: 'deactivated',
        blocked: 'blocked'
      };

      setSuccess(`Driver ${driver.name} has been ${statusLabels[newStatus]} successfully`);
      setShowModal(false);
      setShowBlockModal(false);
      setShowConfirmModal(false);
      setConfirmAction(null);
      setDriverToBlock(null);
      fetchDrivers();
    } catch (err) {
      console.error('Error updating driver:', err);
      setError('Failed to update driver status');
    } finally {
      setProcessing(false);
    }
  };

  // Handle block submission
  const handleBlockSubmit = () => {
    if (!blockReason.trim()) {
      setError('Please provide a reason for blocking this driver');
      return;
    }
    executeStatusChange(driverToBlock, 'blocked', blockReason.trim());
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-ZA', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('en-ZA', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Status badge - updated to support blocked
  const StatusBadge = ({ status }) => {
    const config = {
      active: {
        bg: 'bg-green-100 text-green-800',
        icon: <UserCheck size={14} />,
        label: 'Active'
      },
      inactive: {
        bg: 'bg-orange-100 text-orange-800',
        icon: <UserX size={14} />,
        label: 'Inactive'
      },
      blocked: {
        bg: 'bg-red-100 text-red-800',
        icon: <Ban size={14} />,
        label: 'Blocked'
      }
    };

    const { bg, icon, label } = config[status] || config.inactive;

    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${bg}`}>
        {icon}
        {label}
      </span>
    );
  };

  // Stats
  const stats = {
    total: drivers.length,
    active: drivers.filter(d => d.status === 'active').length,
    inactive: drivers.filter(d => d.status === 'inactive').length,
    blocked: drivers.filter(d => d.status === 'blocked').length
  };

  // Clear messages after 5 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Drivers Management</h1>
          <p className="text-gray-600 mt-1">View and manage all registered drivers</p>
        </div>
        <button
          onClick={fetchDrivers}
          className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
        >
          <RefreshCw size={18} />
          Refresh
        </button>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Check size={18} />
            {success}
          </div>
          <button onClick={() => setSuccess('')}><X size={18} /></button>
        </div>
      )}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertCircle size={18} />
            {error}
          </div>
          <button onClick={() => setError('')}><X size={18} /></button>
        </div>
      )}

      {/* Stats Cards - now 4 cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow-sm p-4">
          <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
          <div className="text-sm text-gray-600">Total Drivers</div>
        </div>
        <div className="bg-green-50 rounded-xl shadow-sm p-4 border border-green-200">
          <div className="text-2xl font-bold text-green-700">{stats.active}</div>
          <div className="text-sm text-green-600">Active</div>
        </div>
        <div className="bg-orange-50 rounded-xl shadow-sm p-4 border border-orange-200">
          <div className="text-2xl font-bold text-orange-700">{stats.inactive}</div>
          <div className="text-sm text-orange-600">Inactive</div>
        </div>
        <div className="bg-red-50 rounded-xl shadow-sm p-4 border border-red-200">
          <div className="text-2xl font-bold text-red-700">{stats.blocked}</div>
          <div className="text-sm text-red-600">Blocked</div>
        </div>
      </div>

      {/* Filters & Search */}
      <div className="bg-white rounded-xl shadow-sm p-4">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Status Filter - now includes 'blocked' */}
          <div className="flex gap-2 flex-wrap">
            {['all', 'active', 'inactive', 'blocked'].map((status) => (
              <button
                key={status}
                onClick={() => setFilter(status)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  filter === status
                    ? status === 'blocked' 
                      ? 'bg-red-600 text-white' 
                      : 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
                {status === 'blocked' && stats.blocked > 0 && filter !== 'blocked' && (
                  <span className="ml-1.5 bg-red-500 text-white text-xs rounded-full px-1.5 py-0.5">
                    {stats.blocked}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name, phone, email, bike reg..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {/* Drivers List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      ) : filteredDrivers.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-12 text-center">
          <Users size={48} className="mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No drivers found</h3>
          <p className="text-gray-600">No drivers match your search criteria</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Driver</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Bike Reg</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Joined</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredDrivers.map((driver) => {
                  const appInfo = driver.driver_applications;
                  const isBlocked = driver.status === 'blocked';
                  return (
                    <tr key={driver.id} className={`hover:bg-gray-50 ${isBlocked ? 'bg-red-50/50' : ''}`}>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="relative">
                            <img
                              src={appInfo?.photo_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(driver.name)}&background=random`}
                              alt={driver.name}
                              className={`w-10 h-10 rounded-full object-cover ${isBlocked ? 'opacity-50 grayscale' : ''}`}
                              onError={(e) => {
                                e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(driver.name)}&background=random`;
                              }}
                            />
                            {isBlocked && (
                              <div className="absolute -bottom-1 -right-1 bg-red-500 rounded-full p-0.5">
                                <Ban size={10} className="text-white" />
                              </div>
                            )}
                          </div>
                          <div>
                            <div className={`font-medium ${isBlocked ? 'text-red-900' : 'text-gray-900'}`}>
                              {driver.name}
                            </div>
                            {isBlocked && driver.blocked_reason && (
                              <div className="text-xs text-red-600 truncate max-w-[200px]" title={driver.blocked_reason}>
                                Reason: {driver.blocked_reason}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">{appInfo?.email || '-'}</div>
                        <div className="text-sm text-gray-500">{driver.phone || appInfo?.phone || '-'}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">
                          {driver.bike_registration || appInfo?.bike_registration || '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <StatusBadge status={driver.status} />
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {formatDate(driver.created_at)}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {/* Quick Toggle Active/Inactive */}
                          {driver.status !== 'blocked' && (
                            <button
                              onClick={() => handleToggleStatus(driver, driver.status === 'active' ? 'inactive' : 'active')}
                              className={`p-2 rounded-lg transition-colors ${
                                driver.status === 'active'
                                  ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                  : 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                              }`}
                              title={driver.status === 'active' ? 'Deactivate driver' : 'Activate driver'}
                            >
                              {driver.status === 'active' ? <ToggleRight size={20} /> : <ToggleLeft size={20} />}
                            </button>
                          )}

                          {/* Block / Unblock Button */}
                          {driver.status === 'blocked' ? (
                            <button
                              onClick={() => handleToggleStatus(driver, 'active')}
                              className="p-2 rounded-lg bg-green-100 text-green-700 hover:bg-green-200 transition-colors"
                              title="Unblock driver"
                            >
                              <ShieldCheck size={20} />
                            </button>
                          ) : (
                            <button
                              onClick={() => handleToggleStatus(driver, 'blocked')}
                              className="p-2 rounded-lg bg-red-100 text-red-700 hover:bg-red-200 transition-colors"
                              title="Block driver"
                            >
                              <ShieldOff size={20} />
                            </button>
                          )}
                          
                          {/* View Details */}
                          <button
                            onClick={() => handleView(driver)}
                            className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-100 text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-200 transition-colors"
                          >
                            <Eye size={16} />
                            View
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ========== DRIVER DETAIL MODAL ========== */}
      {showModal && selectedDriver && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">Driver Details</h2>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X size={20} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              {/* Driver Photo & Name */}
              <div className="flex items-center gap-4 mb-6">
                <div className="relative">
                  <img
                    src={selectedDriver.driver_applications?.photo_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(selectedDriver.name)}&background=random&size=128`}
                    alt={selectedDriver.name}
                    className={`w-20 h-20 rounded-full object-cover border-2 ${
                      selectedDriver.status === 'blocked' ? 'border-red-300 opacity-60 grayscale' : 'border-gray-200'
                    }`}
                  />
                  {selectedDriver.status === 'blocked' && (
                    <div className="absolute -bottom-1 -right-1 bg-red-500 rounded-full p-1">
                      <Ban size={14} className="text-white" />
                    </div>
                  )}
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">{selectedDriver.name}</h3>
                  <StatusBadge status={selectedDriver.status} />
                </div>
              </div>

              {/* Blocked Info Banner */}
              {selectedDriver.status === 'blocked' && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                  <div className="flex items-start gap-3">
                    <Ban size={20} className="text-red-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <h4 className="font-semibold text-red-800 mb-1">Driver Blocked</h4>
                      <p className="text-sm text-red-700 mb-2">
                        <span className="font-medium">Reason:</span> {selectedDriver.blocked_reason || 'No reason provided'}
                      </p>
                      <div className="flex flex-wrap gap-4 text-xs text-red-600">
                        {selectedDriver.blocked_at && (
                          <span className="flex items-center gap-1">
                            <Clock size={12} />
                            Blocked on {formatDateTime(selectedDriver.blocked_at)}
                          </span>
                        )}
                        {selectedDriver.blocked_by && (
                          <span>By: {selectedDriver.blocked_by}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Driver Info */}
              <div className="bg-gray-50 rounded-lg p-4 space-y-3 mb-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="flex items-center gap-2 text-gray-600 mb-1">
                      <Mail size={14} />
                      <span className="text-xs uppercase">Email</span>
                    </div>
                    <div className="font-medium text-gray-900">
                      {selectedDriver.driver_applications?.email || '-'}
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center gap-2 text-gray-600 mb-1">
                      <Phone size={14} />
                      <span className="text-xs uppercase">Phone</span>
                    </div>
                    <div className="font-medium text-gray-900">
                      {selectedDriver.phone || selectedDriver.driver_applications?.phone || '-'}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-xs uppercase text-gray-600 mb-1">ID Number</div>
                    <div className="font-medium text-gray-900">
                      {selectedDriver.id_number || selectedDriver.driver_applications?.id_number || '-'}
                    </div>
                  </div>

                  <div>
                    <div className="text-xs uppercase text-gray-600 mb-1">Bike Registration</div>
                    <div className="font-medium text-gray-900">
                      {selectedDriver.bike_registration || selectedDriver.driver_applications?.bike_registration || '-'}
                    </div>
                  </div>
                </div>

                <div>
                  <div className="text-xs uppercase text-gray-600 mb-1">Joined</div>
                  <div className="font-medium text-gray-900">
                    {formatDate(selectedDriver.created_at)}
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="space-y-3">
                {/* Activate / Reactivate (from inactive or blocked) */}
                {selectedDriver.status !== 'active' && (
                  <button
                    onClick={() => handleToggleStatus(selectedDriver, 'active')}
                    disabled={processing}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors disabled:opacity-50"
                  >
                    {processing ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    ) : (
                      <>
                        {selectedDriver.status === 'blocked' ? <ShieldCheck size={20} /> : <UserCheck size={20} />}
                        {selectedDriver.status === 'blocked' ? 'Unblock & Activate Driver' : 'Activate Driver'}
                      </>
                    )}
                  </button>
                )}

                {/* Deactivate (from active) */}
                {selectedDriver.status === 'active' && (
                  <button
                    onClick={() => handleToggleStatus(selectedDriver, 'inactive')}
                    disabled={processing}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-orange-600 text-white rounded-lg font-medium hover:bg-orange-700 transition-colors disabled:opacity-50"
                  >
                    {processing ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    ) : (
                      <>
                        <UserX size={20} />
                        Deactivate Driver
                      </>
                    )}
                  </button>
                )}

                {/* Block (from active or inactive) */}
                {selectedDriver.status !== 'blocked' && (
                  <button
                    onClick={() => handleToggleStatus(selectedDriver, 'blocked')}
                    disabled={processing}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors disabled:opacity-50"
                  >
                    {processing ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    ) : (
                      <>
                        <Ban size={20} />
                        Block Driver
                      </>
                    )}
                  </button>
                )}

                {/* WhatsApp */}
                {(selectedDriver.phone || selectedDriver.driver_applications?.phone) && (
                  <a
                    href={`https://wa.me/${(selectedDriver.phone || selectedDriver.driver_applications?.phone).replace(/^0/, '27')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-[#25D366] text-white rounded-lg font-medium hover:bg-[#20bd5a] transition-colors"
                  >
                    <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                    Contact on WhatsApp
                  </a>
                )}
              </div>

              {/* Info Warning */}
              {selectedDriver.status === 'active' && (
                <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <AlertCircle size={18} className="text-yellow-600 mt-0.5" />
                    <p className="text-sm text-yellow-700">
                      Deactivating a driver will prevent them from logging into the app.
                      Blocking is more severe — use it for policy violations or misconduct.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ========== BLOCK DRIVER MODAL ========== */}
      {showBlockModal && driverToBlock && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            {/* Header */}
            <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-2 text-red-700">
                <Ban size={22} />
                <h2 className="text-lg font-bold">Block Driver</h2>
              </div>
              <button
                onClick={() => { setShowBlockModal(false); setDriverToBlock(null); setBlockReason(''); }}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X size={20} />
              </button>
            </div>

            {/* Body */}
            <div className="p-6">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-red-800">
                  You are about to block <span className="font-bold">{driverToBlock.name}</span>. 
                  This will immediately prevent them from accepting any deliveries and logging into the driver app.
                </p>
              </div>

              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason for blocking <span className="text-red-500">*</span>
              </label>
              <textarea
                value={blockReason}
                onChange={(e) => setBlockReason(e.target.value)}
                placeholder="e.g. Multiple customer complaints, failed to deliver orders, policy violation..."
                rows={3}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
              />
              <p className="text-xs text-gray-500 mt-1">This reason will be recorded and visible to other admins.</p>

              {/* Quick reason buttons */}
              <div className="flex flex-wrap gap-2 mt-3">
                {[
                  'Customer complaints',
                  'Failed deliveries',
                  'Policy violation',
                  'Misconduct',
                  'No-show / Unreliable'
                ].map((reason) => (
                  <button
                    key={reason}
                    onClick={() => setBlockReason(reason)}
                    className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
                      blockReason === reason
                        ? 'bg-red-100 border-red-300 text-red-700'
                        : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    {reason}
                  </button>
                ))}
              </div>
            </div>

            {/* Footer */}
            <div className="border-t border-gray-200 px-6 py-4 flex gap-3">
              <button
                onClick={() => { setShowBlockModal(false); setDriverToBlock(null); setBlockReason(''); }}
                className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleBlockSubmit}
                disabled={processing || !blockReason.trim()}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {processing ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                ) : (
                  <>
                    <Ban size={18} />
                    Block Driver
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ========== CONFIRMATION MODAL ========== */}
      {showConfirmModal && confirmAction && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-sm w-full">
            <div className="p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-2">{confirmAction.title}</h3>
              <p className="text-sm text-gray-600 mb-6">{confirmAction.message}</p>
              <div className="flex gap-3">
                <button
                  onClick={() => { setShowConfirmModal(false); setConfirmAction(null); }}
                  className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => executeStatusChange(confirmAction.driver, confirmAction.newStatus)}
                  disabled={processing}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-white rounded-lg font-medium transition-colors disabled:opacity-50 ${confirmAction.buttonClass}`}
                >
                  {processing ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  ) : (
                    confirmAction.buttonText
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DriversManagementAdmin;
